import React, {useState} from 'react';
import {UploadFileSelectionDialog} from './UploadFileSelectionDialog';
import {FileMetaDataDialog} from './FileMetaDataDialog';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import {useGetStorageObject} from '../../../hooks/UseGetStorageObject';
import {StorageObject} from '../../../models/StorageObject';
import {DisclaimerDTO} from '../../../models/dto/DisclaimerDTO';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {StorageObjectDTO} from '../../../models/dto/StorageObjectDTO';
import {getPath, isNullOrEmpty} from '../../../utils';

export interface UploadFileDialogProps {
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly disclaimerData: DisclaimerDTO[];
    readonly labelData: LabelDTO[];
    onClose: () => void;
    onSave: (storageObjectDTO: StorageObjectDTO, thumbnail: File | null, executeFileUpload: boolean, file: File, addMore: boolean) => void;
}

/**
 * @name UploadFileDialog
 * @description
 * A dialog that wraps the complete flow to upload a new file
 */
export const UploadFileDialog: React.VFC<UploadFileDialogProps> = (props) => {
    const getStorageObject = useGetStorageObject();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [existingFile, setExistingFile] = useState<StorageObject | null>(null);

    const handleOnSave = (storageObjectDTO: StorageObjectDTO, thumbnail: File | null, addMore: boolean) => {
        if (selectedFile) {
            if ((storageObjectDTO !== undefined) && storageObjectDTO
                && (storageObjectDTO.item !== undefined) && storageObjectDTO.item) {
                if ((storageObjectDTO.item.id === undefined) || (isNullOrEmpty(storageObjectDTO.item.id))) {
                    storageObjectDTO.item.name = selectedFile.name;
                    storageObjectDTO.item.path = decodeURI(props.currentPath);
                    storageObjectDTO.item.fullPath = getPath(storageObjectDTO.item.path, selectedFile.name);
                    storageObjectDTO.item.metadata.contentType = selectedFile.type.trim();
                }

                storageObjectDTO.item.totalSize = selectedFile.size;
            }

            props.onSave(storageObjectDTO, thumbnail, checkExecuteFileUpload(), selectedFile, addMore);
        }
    };

    const onContinueHandler = async (file: File) => {
        if (!existingFile) {
            let path = getPath(props.currentPath, file.name);
            setExistingFile(await getStorageObject(encodeURI(path)));
        }

        setSelectedFile(file);
    };

    const checkExecuteFileUpload = () => {
        return ((existingFile?.name !== selectedFile?.name) || (existingFile?.totalSize !== selectedFile?.size));
    };

    const renderDialogs = () => {
        if (existingFile) {
            return (
                <FileMetaDataDialog
                    item={existingFile}
                    file={selectedFile}
                    parentOwnerRoles={props.parentOwnerRoles}
                    parentRoles={props.parentRoles}
                    parentLocales={props.parentLocales}
                    language={props.language}
                    defaultLocale={props.defaultLocale}
                    disclaimerData={props.disclaimerData}
                    labelData={props.labelData}
                    rolesDataSource={props.rolesDataSource}
                    onClose={props.onClose}
                    onSave={handleOnSave}
                />
            );
        } else if (!selectedFile) {
            return (
                <UploadFileSelectionDialog
                    breadcrumbs={props.breadcrumbs}
                    currentPath={props.currentPath}
                    onClose={props.onClose}
                    onContinue={onContinueHandler}
                />
            );
        } else {
            return (
                <FileMetaDataDialog
                    item={existingFile}
                    file={selectedFile}
                    parentOwnerRoles={props.parentOwnerRoles}
                    parentRoles={props.parentRoles}
                    parentLocales={props.parentLocales}
                    language={props.language}
                    defaultLocale={props.defaultLocale}
                    disclaimerData={props.disclaimerData}
                    labelData={props.labelData}
                    rolesDataSource={props.rolesDataSource}
                    onClose={props.onClose}
                    onSave={handleOnSave}
                />
            );
        }
    };

    return <>{renderDialogs()}</>;
};
